import { Link } from "react-router-dom";
import { Facebook, Instagram, Linkedin, Youtube, Star, MapPin, PawPrint } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const MinimalFooter = () => (
  <footer data-testid="footer-minimal" className="bg-navy text-cream">
    <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-4 px-4 py-8 sm:flex-row sm:px-6 lg:px-8">
      <img src="/logo.png" alt="WhatTheBark logo" className="h-10 w-auto rounded-xl bg-cream p-1.5" />
      <div className="flex items-center gap-5 text-xs text-cream/50">
        <Link to="/terms" data-testid="footer-terms-link" className="transition-colors duration-200 hover:text-bark">
          Terms &amp; Conditions
        </Link>
        <span>© {new Date().getFullYear()} WhatTheBark</span>
      </div>
    </div>
  </footer>
);

const SOCIAL_LINKS = [
  { name: "Facebook", href: "#", Icon: Facebook },
  { name: "Instagram", href: "#", Icon: Instagram },
  { name: "LinkedIn", href: "#", Icon: Linkedin },
  { name: "YouTube", href: "#", Icon: Youtube },
];

export const Footer = () => {
  const { user } = useAuth();
  if (user) return <MinimalFooter />;
  return (
  <footer data-testid="footer-section" className="bg-navy text-cream">
    <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
      <div className="grid gap-12 lg:grid-cols-12">
        <div className="lg:col-span-4">
          <div className="flex items-center">
            <img
              src="/logo.png"
              alt="WhatTheBark logo"
              className="h-14 w-auto rounded-2xl bg-cream p-2"
            />
          </div>
          <p className="mt-6 max-w-sm text-sm leading-relaxed text-cream/60">
            Doggy day care and boarding built while keeping pups and their needs in mind —
            freshly cooked meals, supervised play and people who genuinely love dogs.
          </p>
          <div className="mt-6 flex gap-3">
            {SOCIAL_LINKS.map((s) => (
              <a
                key={s.name}
                href={s.href}
                data-testid={`footer-social-${s.name.toLowerCase()}`}
                aria-label={`${s.name} (link coming soon)`}
                className="flex h-10 w-10 items-center justify-center rounded-full border border-cream/20 text-cream/70 transition-colors duration-200 hover:border-bark hover:text-bark"
              >
                <s.Icon className="h-4 w-4" aria-hidden="true" />
              </a>
            ))}
          </div>
        </div>

        <div className="lg:col-span-2">
          <h3 className="text-xs font-semibold uppercase tracking-[0.24em] text-cream/40">
            Explore
          </h3>
          <ul className="mt-4 space-y-3 text-sm font-medium">
            {[
              { to: "/", label: "Home" },
              { to: "/services", label: "Services" },
              { to: "/about", label: "About Us" },
              { to: "/blogs", label: "Blogs" },
              { to: "/signin", label: "Customer Login" },
            ].map((l) => (
              <li key={l.to}>
                <Link
                  to={l.to}
                  data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="text-cream/70 transition-colors duration-200 hover:text-bark"
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-3">
          <h3 className="text-xs font-semibold uppercase tracking-[0.24em] text-cream/40">
            Connect with us
          </h3>
          <div className="mt-4 space-y-3">
            <div
              data-testid="footer-google-reviews"
              className="flex items-center gap-3 rounded-2xl border border-cream/10 bg-white/5 p-4"
            >
              <Star className="h-5 w-5 shrink-0 text-amberwarm" aria-hidden="true" />
              <div className="text-sm">
                <p className="font-semibold text-cream">Google Reviews</p>
                <p className="text-cream/50">Live review embed coming soon</p>
              </div>
            </div>
            <div
              data-testid="footer-google-maps"
              className="flex items-center gap-3 rounded-2xl border border-cream/10 bg-white/5 p-4"
            >
              <MapPin className="h-5 w-5 shrink-0 text-bark" aria-hidden="true" />
              <div className="text-sm">
                <p className="font-semibold text-cream">Find us on Google Maps</p>
                <p className="text-cream/50">Map embed coming soon</p>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-3">
          <h3 className="text-xs font-semibold uppercase tracking-[0.24em] text-cream/40">
            Good to know
          </h3>
          <div className="mt-4 space-y-3">
            <Link
              to="/terms"
              data-testid="footer-terms-link"
              className="block rounded-2xl border border-cream/10 bg-white/5 p-4 transition-colors duration-200 hover:border-cream/25"
            >
              <p className="text-sm font-semibold text-cream">Terms &amp; Conditions</p>
              <p className="text-sm text-cream/50">The fine print, in plain words. Publishing shortly.</p>
            </Link>
            <Link
              to="/franchise"
              data-testid="footer-franchise-link"
              className="block rounded-2xl border border-cream/10 bg-white/5 p-4 transition-colors duration-200 hover:border-bark/50"
            >
              <p className="flex items-center gap-2 text-sm font-semibold text-cream">
                <PawPrint className="h-4 w-4 text-amberwarm" aria-hidden="true" />
                Franchise Options
              </p>
              <p className="text-sm text-cream/50">
                Bring the pack to your city. Register your interest.
              </p>
            </Link>
          </div>
        </div>
      </div>

      <div className="mt-14 flex flex-col items-center justify-between gap-4 border-t border-cream/10 pt-8 text-xs text-cream/40 sm:flex-row">
        <p>© {new Date().getFullYear()} — All rights reserved.</p>
        <p className="font-display italic text-cream/50">Made with love, for pups</p>
      </div>
    </div>
  </footer>
  );
};
